/**
 * Application Routes Configuration
 * Centralized route composition for the application
 */
import { isElectron } from '@/renderer/util';
import OnboardingFlow from '@components/onboarding/OnboardingFlow';
import { SalesPage } from '@components/sales';
import { useAppSelector } from '@hooks/useRedux';
import GuestReviewPage from '@pages/GuestReviewPage';
import { KitchenDisplayPage } from '@pages/KitchenDisplayPage';
import LandingPage from '@pages/LandingPage';
import { AccessControlCode } from '@shared/constants';
import { OnboardingStep } from '@shared/types';
import React, { useEffect, useMemo } from 'react';
import {
  BrowserRouter,
  MemoryRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { guestRoutePaths, publicRoutePaths } from '../lib/utils';
import {
  selectAuthInitialized,
  selectCurrentUser,
  selectHasAccessControl,
  selectHasActiveSubscription,
  selectIsKds,
  selectIsReceptionist,
  selectNeedsFirstTimeOnboarding,
  selectOutletHasKdsEnabled,
  selectPendingGoogleUser,
  selectPreferredDashboardRoute,
} from '../store/auth/authSelectors';
import { selectOnboardingStep } from '../store/selectors';
import { createAccommodationRoutes } from './accommodationRoutes';
import { createAccountingRoutes } from './accountingRoutes';
import { createDashboardRoutes, dashboardRoutes } from './dashboardRoutes';
import { AccessControlledDashboardPage } from './featureRouteHelpers';
import { createInventoryRoutes } from './inventoryRoutes';
import { useSettingsRoutes } from './settingsRoutes';

const Router = isElectron() ? MemoryRouter : BrowserRouter;

interface AppRoutesProps {
  onAppReady: () => void;
}

/**
 * Inner component that uses router hooks
 */
const AppRoutesInner: React.FC<AppRoutesProps> = ({ onAppReady }) => {
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);

  // Activate inactivity auto-lock
  // useAutoLockTimeout(currentUser);

  const isAuthInitialized = useAppSelector(selectAuthInitialized);
  const location = useLocation();
  const currentLocation = useMemo(() => location.pathname, [location]);
  const hasActiveSubscription = useAppSelector(selectHasActiveSubscription);
  const needsFirstTimeOnboarding = useAppSelector(
    selectNeedsFirstTimeOnboarding,
  );
  const onboardingStep = useAppSelector(selectOnboardingStep);
  const pendingGoogleUser = useAppSelector(selectPendingGoogleUser);
  const isReceptionist = useAppSelector(selectIsReceptionist);
  const isKds = useAppSelector(selectIsKds);
  const outletHasKds = useAppSelector(selectOutletHasKdsEnabled);
  const hasAnySalesAccess = useAppSelector(
    selectHasAccessControl(AccessControlCode.SALES_ORDERS_CREATE),
  );
  const preferredDashboardRoute = useAppSelector(selectPreferredDashboardRoute);

  useEffect(() => {
    if (!isAuthInitialized) return;

    // New Google user: Supabase session exists but no app profile yet.
    // Route them to onboarding so they can set up a company/outlet.
    if (pendingGoogleUser && !currentUser) {
      navigate('/onboarding');
      onAppReady();
      return;
    }

    if (currentUser && publicRoutePaths.includes(currentLocation)) {
      // Review page is open to all — never redirect away from it
      if (currentLocation === '/review') {
        onAppReady();
        return;
      }

      const shouldRouteToFirstTimeOnboarding =
        needsFirstTimeOnboarding &&
        (currentLocation === '/' || currentLocation === '/onboarding');

      if (shouldRouteToFirstTimeOnboarding) {
        navigate('/dashboard/getting-started');
        onAppReady();
        return;
      }

      const shouldStayOnOnboarding =
        currentLocation === '/onboarding' &&
        onboardingStep >= OnboardingStep.PAYMENT_SETUP &&
        !hasActiveSubscription;

      if (shouldStayOnOnboarding) {
        onAppReady();
        return;
      }

      if (currentUser?.outlet_id) {
        if (hasActiveSubscription) {
          if (!hasAnySalesAccess && preferredDashboardRoute) {
            navigate(preferredDashboardRoute);
          } else {
            const goToAccommodation = isReceptionist && !hasAnySalesAccess;
            const goToKds = isKds && !hasAnySalesAccess;
            navigate(
              goToKds
                ? '/kitchen-display'
                : goToAccommodation
                  ? '/accommodation/dashboard'
                  : '/sales',
            );
          }
        } else {
          navigate('/onboarding');
        }
      } else {
        navigate('/onboarding');
      }
    } else if (!currentUser && !guestRoutePaths.includes(currentLocation)) {
      // Signed out (incl. an expired session) anywhere but a guest route —
      // always back to the login page.
      navigate('/', { replace: true });
    }
    onAppReady();
  }, [
    isAuthInitialized,
    currentLocation,
    currentUser,
    pendingGoogleUser,
    needsFirstTimeOnboarding,
    onboardingStep,
    navigate,
    hasActiveSubscription,
    isReceptionist,
    isKds,
    hasAnySalesAccess,
    preferredDashboardRoute,
    onAppReady,
  ]);

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/onboarding" element={<OnboardingFlow />} />
      <Route path="/review" element={<GuestReviewPage />} />
      <Route
        path="/sales"
        element={
          !hasAnySalesAccess && preferredDashboardRoute ? (
            <Navigate to={preferredDashboardRoute} replace />
          ) : isKds && !hasAnySalesAccess ? (
            <Navigate to="/kitchen-display" replace />
          ) : isReceptionist && !hasAnySalesAccess ? (
            <Navigate to="/accommodation/dashboard" replace />
          ) : (
            <SalesPage />
          )
        }
      />
      <Route
        path="/kitchen-display"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.DASHBOARD_KDS_ACCESS}
            title="Kitchen Display Screen access restricted"
            description="Your account does not currently have permission to access the Kitchen Display Screen."
            outletCapability={{
              enabled: outletHasKds,
              title: 'Kitchen Display Screen not available',
              description:
                'This outlet has not enabled the Kitchen Display Screen. Contact your administrator if you believe this outlet supports it.',
            }}
          >
            <KitchenDisplayPage />
          </AccessControlledDashboardPage>
        }
      />

      {createDashboardRoutes()}
      {createAccommodationRoutes()}
      {createAccountingRoutes()}
      {createInventoryRoutes()}
      {useSettingsRoutes()}
    </Routes>
  );
};

/**
 * Main AppRoutes component with Router wrapper
 */
export const AppRoutes: React.FC<AppRoutesProps> = ({ onAppReady }) => (
  <Router>
    <AppRoutesInner onAppReady={onAppReady} />
  </Router>
);

export { dashboardRoutes };
